// src/App.js
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import fontkit from "@pdf-lib/fontkit";
import { Rnd } from "react-rnd";
import { PDFDocument, rgb } from "pdf-lib";
import { useDropzone } from "react-dropzone";
import "./App.css";

import {
    ThemeProvider,
    createTheme,
    CssBaseline,
    AppBar,
    Toolbar,
    Container,
    useMediaQuery,
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    Button,
    Stack,
    Tooltip,
    IconButton,
    CircularProgress,
    Drawer,
    Divider,
    Snackbar,
    Alert,
    Fab,
    Paper,
    Chip,
    Avatar,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { useAuth } from "./AuthService";

// ===== Public base (for hosting under /B2B-Sales) =====
const PUBLIC = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

// ===== PDF.js worker =====
pdfjs.GlobalWorkerOptions.workerSrc = `${PUBLIC}/pdf.worker.min.js`;

// ===== Defaults =====
const DEFAULT_LOGO_STATE = { x: 150, y: 10, width: 80, height: 80 };
const DEFAULT_COUNTRY = "uk";

// ===== AAD group gating =====
const REQUIRED_GROUP_ID = "50383d04-3da5-4339-9dad-b0d440e3b87d";
const ADMIN_GROUP_ID = "00000000-0000-0000-0000-000000000000";
const ADMIN_FALLBACK_UPNS = ["ash.caveney@upfitness.com"];

// Minimal scopes for gate
const GRAPH_SCOPES = ["User.Read", "GroupMember.Read.All"];

// ===== Admin log filters =====
const ADMIN_EVENTS = new Set([
    "pdf_generated",
    "signin_success",
    "login_success",
    "access_denied",
    "group_check_pass",
    "support_request_sent",
    "support_request_failed",
    "sp_upload_success",
    "sp_upload_failed",
]);

const normaliseEvent = (e) => {
    const m = { login_success: "signin_success" };
    return { ...e, event: m[e.event] || e.event };
};

// ===== Helpers =====
function generatePromoCode(companyName) {
    if (!companyName) return "";
    const year = String(new Date().getFullYear()).slice(-2);
    const maxLen = 11 - year.length;
    const words = companyName
        .split(" ")
        .filter(Boolean)
        .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
    let code = "";
    for (const word of words) {
        if ((code + word).length <= maxLen) code += word;
        else break;
    }
    return code + year;
}

async function logEvent(payload) {
    try {
        await fetch("/b2b-log", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ app: "b2b-pdf", ...payload }),
            keepalive: true,
        });
    } catch {
        /* ignore */
    }
}

// Heuristic single-line fit (kept for potential labels)
function fitSingleLineFont(label, basePx, maxWidthPx) {
    if (!label) return basePx;
    const safe = label.trim();
    if (!safe) return basePx;
    const estCharWidth = 0.58;
    let size = basePx;
    const pad = 16;
    const target = Math.max(24, maxWidthPx - pad * 2);
    while (safe.length * estCharWidth * size > target && size > 10) size -= 1;
    return size;
}

// Multiline fit — shrinks until fits width & height
function fitMultilineFont(
    text,
    basePx,
    maxWidthPx,
    maxHeightPx,
    maxLines = 4,
    fontFamily = "Montserrat",
    fontWeight = 700
) {
    const canvas =
        fitMultilineFont._canvas ||
        (fitMultilineFont._canvas = document.createElement("canvas"));
    const ctx = canvas.getContext("2d");
    const words = String(text || "")
        .trim()
        .split(/\s+/);
    if (!words.length) return { size: basePx, lineHeight: basePx * 1.16, lines: [] };

    let size = basePx;
    while (size >= 10) {
        ctx.font = `${fontWeight} ${size}px ${fontFamily}`;
        const lines = [];
        let line = "";

        for (const w of words) {
            const candidate = line ? `${line} ${w}` : w;
            if (ctx.measureText(candidate).width <= maxWidthPx) {
                line = candidate;
            } else {
                if (line) lines.push(line);
                line = w;
            }
        }
        if (line) lines.push(line);

        const lineHeight = size * 1.16;
        const fitsHeight = lines.length * lineHeight <= maxHeightPx;
        const fitsLines = lines.length <= maxLines;

        if (fitsHeight && fitsLines) return { size, lineHeight, lines };
        size -= 1;
    }
    return { size: 10, lineHeight: 10 * 1.16, lines: [] };
}

// ===== Template picker (respects exclusive offer toggle) =====
const getTemplatePath = (region, exclusiveOn) => {
    const isUK = region === "uk";
    if (exclusiveOn) {
        return `${PUBLIC}/${isUK ? "UK-Template-No-Offer.pdf" : "US-Template-No-Offer.pdf"}`;
    }
    return `${PUBLIC}/${isUK ? "uk-template.pdf" : "us-template.pdf"}`;
};

export default function App() {
    // ===== Auth =====
    const { isAuthenticated, login, logout, account, inProgress, instance } = useAuth();

    // ===== Group-gate state =====
    const [checkingAccess, setCheckingAccess] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [accessError, setAccessError] = useState(null);

    // ===== Profile (Graph) =====
    const [jobTitle, setJobTitle] = useState("");
    const [photoUrl, setPhotoUrl] = useState("");
    const [photoLoading, setPhotoLoading] = useState(false);

    // ===== Admin console state =====
    const [adminOpen, setAdminOpen] = useState(false);
    const [adminRows, setAdminRows] = useState([]);
    const [adminLoading, setAdminLoading] = useState(false);
    const [adminErr, setAdminErr] = useState("");

    // ===== PDF Editor state =====
    const [country, setCountry] = useState(DEFAULT_COUNTRY);
    const [companyName, setCompanyName] = useState("");

    // NEW: allow generation without logo
    const [skipLogo, setSkipLogo] = useState(false);

    const [logoFile, setLogoFile] = useState(null);
    const [logoURL, setLogoURL] = useState(null);
    const [logoState, setLogoState] = useState(DEFAULT_LOGO_STATE);
    const [appliedLogoState, setAppliedLogoState] = useState(DEFAULT_LOGO_STATE);
    const [editMode, setEditMode] = useState(false);
    const [lockAspect, setLockAspect] = useState(true);
    const [pageWidth, setPageWidth] = useState(595);
    const [pageHeight, setPageHeight] = useState(842);
    const [logoApplied, setLogoApplied] = useState(false);
    const pageRef = useRef(null);

    const isPngLogo = logoFile && logoFile.type === "image/png";
    const hasCompany = companyName.trim().length > 0;

    // UPDATED: allow download when skipLogo=true OR logoApplied=true
    const canDownload = hasCompany && !editMode && (skipLogo || logoApplied);

    // ===== Help / Support drawer =====
    const HELP_SUBJECT = "Support Request – PDF Generator";
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpBody, setHelpBody] = useState("");
    const [helpSending, setHelpSending] = useState(false);
    const [helpSnack, setHelpSnack] = useState({ open: false, ok: true, msg: "" });

    // ===== Theme / dark mode =====
    const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
    const [mode, setMode] = useState(prefersDark ? "dark" : "light");

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: { main: "#e2231a" },
                    secondary: { main: "#111" },
                    background: {
                        default: mode === "dark" ? "#0f1011" : "#fafafa",
                        paper: mode === "dark" ? "#151618" : "#ffffff",
                    },
                },
                shape: { borderRadius: 12 },
                typography: {
                    fontFamily:
                        "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
                    h5: { fontWeight: 700 },
                },
                components: {
                    MuiPaper: {
                        defaultProps: { elevation: 0 },
                        styleOverrides: {
                            root: {
                                border: "1px solid",
                                borderColor:
                                    mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                            },
                        },
                    },
                    MuiButton: { styleOverrides: { root: { borderRadius: 12 } } },
                },
            }),
        [mode]
    );

    // ===== Admin: read log (NDJSON) =====
    async function fetchAdminEvents(limit = 200) {
        const resp = await fetch(`/b2b-log-read?limit=${limit}`, {
            cache: "no-store",
            headers: { Accept: "text/plain" },
        });
        if (!resp.ok) throw new Error(`Log read ${resp.status}`);
        const text = await resp.text();
        const rows = text
            .split("\n")
            .filter(Boolean)
            .map((line) => {
                try {
                    return JSON.parse(line);
                } catch {
                    return null;
                }
            })
            .filter(Boolean)
            .map(normaliseEvent)
            .filter((e) => ADMIN_EVENTS.has(e.event))
            .map((e) => ({
                ts: e.ts || e.timestamp,
                event: e.event,
                company: e.company || "",
                promo: e.promo || e.promocode || e.code || "",
                by: e.by || e.upn || e.user || "",
                note: e.note || e.message || "",
            }));
        return rows;
    }

    const loadAdmin = async () => {
        setAdminLoading(true);
        setAdminErr("");
        try {
            const rows = await fetchAdminEvents(300);
            setAdminRows(rows);
        } catch (e) {
            setAdminErr(e?.message || "Failed to load log");
        } finally {
            setAdminLoading(false);
        }
    };

    // ===== Pull profile (job title + photo) =====
    const fetchProfile = async () => {
        try {
            const tokenResp = await instance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: instance.getAllAccounts()[0],
            });

            const me = await fetch("https://graph.microsoft.com/v1.0/me?$select=jobTitle", {
                headers: { Authorization: `Bearer ${tokenResp.accessToken}` },
            }).then((r) => r.json());
            setJobTitle(me?.jobTitle || "");

            setPhotoLoading(true);
            const photoResp = await fetch("https://graph.microsoft.com/v1.0/me/photos/64x64/$value", {
                headers: { Authorization: `Bearer ${tokenResp.accessToken}` },
            });
            if (photoResp.ok) {
                const blob = await photoResp.blob();
                const url = URL.createObjectURL(blob);
                setPhotoUrl(url);
            } else {
                setPhotoUrl("");
            }
        } catch {
            setPhotoUrl("");
        } finally {
            setPhotoLoading(false);
        }
    };

    // ===== Logo handlers =====
    const handleLogoChange = (file) => {
        if (file) {
            // if user uploads a logo, implicitly disable skip
            setSkipLogo(false);

            setLogoFile(file);
            setLogoURL(URL.createObjectURL(file));
            setLogoState(DEFAULT_LOGO_STATE);
            setAppliedLogoState(DEFAULT_LOGO_STATE);
            setEditMode(true);
            setLogoApplied(false);
        }
    };

    const fileInputOnChange = (e) => {
        const file = e.target.files?.[0];
        if (file) handleLogoChange(file);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: false,
        noClick: true,
        noKeyboard: true,
        accept: { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"] },
        onDrop: (files) => {
            const [file] = files;
            handleLogoChange(file);
        },
    });

    const handleApplyLogo = () => {
        setAppliedLogoState((s) => {
            const pos = clampDrag(s.x, s.y, s.width, s.height);
            return { ...s, ...pos };
        });
        setEditMode(false);
        setLogoApplied(true);
    };

    const handleLogoClick = () => {
        if (!editMode && logoApplied) setEditMode(true);
    };

    const onPageRenderSuccess = (page) => {
        const { width, height } = page.originalWidth
            ? { width: page.originalWidth, height: page.originalHeight }
            : { width: page.width, height: page.height };
        setPageWidth(width);
        setPageHeight(height);
    };

    // ===== Clamp to top 25% region (HARD STOP) =====
    const previewBoxDims = useMemo(
        () => ({ w: pageWidth / 1.5, h: pageHeight / 1.5 }),
        [pageWidth, pageHeight]
    );
    const allowedLogoBottom = Math.floor(previewBoxDims.h * 0.25);

    function clampDrag(x, y, width, height) {
        const maxX = previewBoxDims.w - width;
        const maxY = allowedLogoBottom - height;
        return {
            x: Math.max(0, Math.min(x, maxX)),
            y: Math.max(0, Math.min(y, Math.max(0, maxY))),
        };
    }

    function clampResize(width, height, x, y) {
        const maxW = Math.max(10, previewBoxDims.w - x);
        const maxH = Math.max(10, allowedLogoBottom - y);
        return {
            width: Math.max(10, Math.min(width, maxW)),
            height: Math.max(10, Math.min(height, maxH)),
            x,
            y,
        };
    }

    // ===== Group-gate check =====
    const checkAccess = async () => {
        try {
            setCheckingAccess(true);
            setAccessError(null);

            const tokenResp = await instance.acquireTokenSilent({
                scopes: GRAPH_SCOPES,
                account: instance.getAllAccounts()[0],
            });

            await logEvent({
                event: "signin_success",
                upn: account?.username,
                name: account?.name,
                tid: account?.tenantId,
            });

            let url = "https://graph.microsoft.com/v1.0/me/transitiveMemberOf?$select=id&$top=999";
            let foundUser = false;
            let foundAdmin = false;

            while (url && (!foundUser || !foundAdmin)) {
                const r = await fetch(url, {
                    headers: { Authorization: `Bearer ${tokenResp.accessToken}` },
                });
                if (!r.ok) throw new Error(`Graph ${r.status}: ${await r.text()}`);
                const j = await r.json();
                const ids = (j.value || []).map((g) => g.id);
                if (!foundUser && ids.includes(REQUIRED_GROUP_ID)) foundUser = true;
                if (ADMIN_GROUP_ID !== "00000000-0000-0000-0000-000000000000") {
                    if (!foundAdmin && ids.includes(ADMIN_GROUP_ID)) foundAdmin = true;
                }
                url = j["@odata.nextLink"];
            }

            const upn = (account?.username || "").toLowerCase();
            const allowAdminByUpn = ADMIN_FALLBACK_UPNS.map((s) => s.toLowerCase()).includes(upn);

            setHasAccess(foundUser);
            setIsAdmin(foundAdmin || allowAdminByUpn);

            if (!foundUser) {
                await logEvent({
                    event: "access_denied",
                    upn: account?.username,
                    name: account?.name,
                    reason: "not in required group",
                });
            } else {
                await logEvent({
                    event: "group_check_pass",
                    upn: account?.username,
                    name: account?.name,
                    requiredGroup: REQUIRED_GROUP_ID,
                });
            }
        } catch (e) {
            setAccessError(e?.message || "Access check failed");
            setHasAccess(false);
            await logEvent({
                event: "access_denied",
                upn: account?.username,
                name: account?.name,
                reason: "access check error",
            });
        } finally {
            setCheckingAccess(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            checkAccess();
            fetchProfile();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // ===== Send support email as signed-in user =====
    async function sendSupportEmail({ subject, bodyHtml }) {
        const acct = instance.getAllAccounts()[0];
        let token;
        try {
            token = await instance.acquireTokenSilent({
                scopes: ["Mail.Send"],
                account: acct,
            });
        } catch {
            token = await instance.acquireTokenPopup({
                scopes: ["Mail.Send"],
                account: acct,
                prompt: "consent",
            });
        }

        const r = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token.accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                message: {
                    subject,
                    body: { contentType: "HTML", content: bodyHtml },
                    toRecipients: [{ emailAddress: { address: "itsupport@upfitness.com" } }],
                },
                saveToSentItems: true,
            }),
        });

        if (!r.ok) {
            const t = await r.text().catch(() => "");
            throw new Error(`Graph sendMail ${r.status}: ${t}`);
        }
    }

    const handleOpenHelp = () => {
        setHelpBody("");
        setHelpOpen(true);
    };

    const handleSendHelp = async () => {
        if (helpBody.trim().length < 3) {
            setHelpSnack({ open: true, ok: false, msg: "Please add a couple of details." });
            return;
        }
        setHelpSending(true);
        try {
            const who = `${account?.name || "User"}${account?.username ? ` (${account.username})` : ""}`;
            const summary = `
        <div style="font-family:Arial,Helvetica,sans-serif;font-size:14px">
          <h3 style="margin:0 0 8px 0">B2B PDF Tool – Support context</h3>
          <table cellspacing="0" cellpadding="6" style="border-collapse:collapse">
            <tr><td><b>User</b></td><td>${who}</td></tr>
            <tr><td><b>Job title</b></td><td>${jobTitle || "-"}</td></tr>
            <tr><td><b>Region</b></td><td>${country?.toUpperCase()}</td></tr>
            <tr><td><b>Company</b></td><td>${companyName || "-"}</td></tr>
            <tr><td><b>Promo</b></td><td>${generatePromoCode(companyName) || "-"}</td></tr>
            <tr><td><b>Skip Logo</b></td><td>${skipLogo ? "Yes" : "No"}</td></tr>
          </table>
          <hr style="margin:14px 0;border:none;border-top:1px solid #eee"/>
          <div>
            <div style="font-weight:bold;margin-bottom:6px">Issue details</div>
            <div>${helpBody.replace(/\n/g, "<br/>")}</div>
          </div>
        </div>
      `;
            await sendSupportEmail({ subject: HELP_SUBJECT, bodyHtml: summary });
            setHelpOpen(false);
            setHelpSnack({ open: true, ok: true, msg: "Support request sent." });
            await logEvent({
                event: "support_request_sent",
                by: account?.username,
                company: companyName || "",
                promo: generatePromoCode(companyName) || "",
            });
        } catch (e) {
            setHelpSnack({
                open: true,
                ok: false,
                msg: "Failed to send. If this persists, contact IT Service Desk.",
            });
            await logEvent({
                event: "support_request_failed",
                by: account?.username,
                note: String(e?.message || e),
            });
        } finally {
            setHelpSending(false);
        }
    };

    // ===== AFTER-DOWNLOAD: SharePoint Upload Popout =====
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadBlob, setUploadBlob] = useState(null);
    const [uploadFilename, setUploadFilename] = useState("");
    const [spUploading, setSpUploading] = useState(false);

    async function uploadToSharePoint() {
        if (!uploadBlob || !uploadFilename || !companyName.trim()) return;
        setSpUploading(true);
        try {
            const form = new FormData();
            form.append("file", uploadBlob, uploadFilename);
            form.append("companyName", companyName.trim());
            form.append("overwrite", "true");

            const r = await fetch("/sp-upload", { method: "POST", body: form });
            const text = await r.text();
            let data = null;
            try {
                data = JSON.parse(text);
            } catch (_) { }

            if (!r.ok || !data?.ok) {
                throw new Error(data?.error || text || `HTTP ${r.status}`);
            }

            const dest = `${data.folderPath}/${data.filename}`;
            setUploadDialogOpen(false);
            setHelpSnack({ open: true, ok: true, msg: `Uploaded: ${dest}` });

            await logEvent({
                event: "sp_upload_success",
                by: account?.username,
                company: companyName,
                filename: uploadFilename,
                note: dest,
            });
        } catch (e) {
            setHelpSnack({ open: true, ok: false, msg: `Upload failed: ${e.message}` });
            await logEvent({
                event: "sp_upload_failed",
                by: account?.username,
                company: companyName,
                note: String(e?.message || e),
            });
        } finally {
            setSpUploading(false);
        }
    }

    // ===== Exclusive offer state (single box) =====
    const [customizeOffer, setCustomizeOffer] = useState(false); // toggle
    const [exclusiveOfferText, setExclusiveOfferText] = useState("Pay no sign-up fee");
    // positive moves down, negative moves up (in pixels on the PREVIEW)
    const [exclusiveYOffset, setExclusiveYOffset] = useState(-40);

    // ===== Loading/auth guard screens =====
    if (inProgress !== "none") {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box
                    sx={{
                        height: "100vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <CircularProgress />
                    <Typography>Loading authentication…</Typography>
                </Box>
            </ThemeProvider>
        );
    }

    if (!isAuthenticated) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Container maxWidth="sm" sx={{ py: 6 }}>
                    <Paper sx={{ p: 4, textAlign: "center" }}>
                        <img
                            src={`${PUBLIC}/UP_Logo_Horizontal_RGB_White.png`}
                            alt="Ultimate Performance Logo"
                            style={{ width: 260, marginBottom: 18 }}
                        />
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                            UP B2B PDF Generator
                        </Typography>
                        <Typography sx={{ color: "text.secondary", mb: 3 }}>
                            Generate branded, region-specific B2B documents in seconds. Sign in with your UP
                            Microsoft 365 account to continue.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => {
                                logEvent({ event: "login_start" });
                                login();
                            }}
                            sx={{ textTransform: "none", px: 4, py: 1.3, borderRadius: 5 }}
                        >
                            Sign in with Microsoft
                        </Button>
                    </Paper>
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.disabled",
                            mt: 2,
                            display: "block",
                            textAlign: "center",
                        }}
                    >
                        © {new Date().getFullYear()} Ultimate Performance – Internal Tools
                    </Typography>
                </Container>
            </ThemeProvider>
        );
    }

    if (checkingAccess) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box
                    sx={{
                        height: "100vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                    }}
                >
                    <CircularProgress size={22} />
                    <Typography>
                        Checking access for <strong>{account?.name}</strong>…
                    </Typography>
                </Box>
            </ThemeProvider>
        );
    }

    if (!hasAccess) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box
                    sx={{
                        height: "100vh",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        gap: 2,
                        px: 2,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Access denied
                    </Typography>
                    <Typography sx={{ maxWidth: 520 }}>
                        Your account <strong>{account?.name}</strong>
                        {account?.username ? ` (${account.username})` : ""} does not have access to this
                        application. Please contact itsupport@upfitness.com to request access
                    </Typography>
                    {accessError ? (
                        <Typography sx={{ color: "error.main", whiteSpace: "pre-wrap" }}>
                            {accessError}
                        </Typography>
                    ) : null}
                    <Button variant="outlined" onClick={logout}>
                        Logout
                    </Button>
                </Box>
            </ThemeProvider>
        );
    }

    // ===== Main App =====
    // exclusiveOn is the computed flag the template picker uses
    const exclusiveOn = customizeOffer === true;

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />

            {/* Header / AppBar */}
            <AppBar
                position="sticky"
                color="transparent"
                elevation={0}
                sx={{
                    backdropFilter: "saturate(180%) blur(6px)",
                    borderBottom: (t) =>
                        `1px solid ${t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"
                        }`,
                }}
            >
                <Toolbar sx={{ px: { xs: 2, md: 4 }, gap: 1 }}>
                    <img
                        src={`${PUBLIC}/UP_Logo_Horizontal_RGB_White.png`}
                        alt="UP"
                        style={{ height: 28, marginRight: 8 }}
                    />
                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
                        B2B PDF Generator
                    </Typography>

                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mr: 1 }}>
                        {photoLoading ? (
                            <Skeleton variant="circular" width={32} height={32} />
                        ) : (
                            <Avatar src={photoUrl || undefined} alt={account?.name} sx={{ width: 32, height: 32 }} />
                        )}
                        <Box sx={{ textAlign: "right" }}>
                            <Typography sx={{ fontSize: 13, lineHeight: 1 }}>
                                {account?.name || "User"}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1 }}>
                                {jobTitle || account?.username || ""}
                            </Typography>
                        </Box>
                    </Stack>

                    <Button variant="outlined" onClick={logout} sx={{ textTransform: "none", mr: 1 }}>
                        Logout
                    </Button>

                    {isAdmin && (
                        <Button
                            variant="outlined"
                            onClick={async () => {
                                if (!adminOpen) await loadAdmin();
                                setAdminOpen((s) => !s);
                            }}
                            sx={{ textTransform: "none", mr: 1 }}
                        >
                            {adminOpen ? "Close Admin" : "Open Admin"}
                        </Button>
                    )}

                    <Button
                        size="small"
                        variant="text"
                        onClick={() => setMode((m) => (m === "dark" ? "light" : "dark"))}
                        sx={{ textTransform: "none", opacity: 0.8 }}
                    >
                        {mode === "dark" ? "Light mode" : "Dark mode"}
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 3 }}>
                {/* Admin Console */}
                {isAdmin && adminOpen && (
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Admin Console — Recent Events
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Shows <code>pdf_generated</code>, <code>signin_success</code>, <code>access_denied</code>,
                            support and SharePoint upload events.
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <Button variant="outlined" onClick={loadAdmin} disabled={adminLoading}>
                                {adminLoading ? "Refreshing…" : "Refresh"}
                            </Button>
                            {adminErr && <Typography color="error.main">{adminErr}</Typography>}
                        </Stack>

                        <Box sx={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ textAlign: "left" }}>
                                        <th style={{ borderBottom: "1px solid #ddd", padding: 8 }}>Timestamp</th>
                                        <th style={{ borderBottom: "1px solid #ddd", padding: 8 }}>Event</th>
                                        <th style={{ borderBottom: "1px solid #ddd", padding: 8 }}>Company</th>
                                        <th style={{ borderBottom: "1px solid #ddd", padding: 8 }}>Promo Code</th>
                                        <th style={{ borderBottom: "1px solid #ddd", padding: 8 }}>Created By</th>
                                        <th style={{ borderBottom: "1px solid #ddd", padding: 8 }}>Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {adminRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} style={{ padding: 12, color: "#666" }}>
                                                No events yet.
                                            </td>
                                        </tr>
                                    ) : (
                                        adminRows.map((r, i) => (
                                            <tr key={i}>
                                                <td style={{ borderBottom: "1px solid #f0f0f0", padding: 8 }}>{r.ts}</td>
                                                <td style={{ borderBottom: "1px solid #f0f0f0", padding: 8 }}>{r.event}</td>
                                                <td style={{ borderBottom: "1px solid #f0f0f0", padding: 8 }}>{r.company}</td>
                                                <td style={{ borderBottom: "1px solid #f0f0f0", padding: 8 }}>{r.promo}</td>
                                                <td style={{ borderBottom: "1px solid #f0f0f0", padding: 8 }}>{r.by}</td>
                                                <td style={{ borderBottom: "1px solid #f0f0f0", padding: 8 }}>{r.note}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </Box>
                    </Paper>
                )}

                {/* Main layout */}
                <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="stretch">
                    {/* Config panel */}
                    <Paper sx={{ p: 3, flex: 1 }}>
                        <Typography variant="h5" sx={{ mb: 0.5 }}>
                            UP B2B PDF Generator
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Choose region, enter company, upload & place logo, then download.
                        </Typography>

                        <Stack spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel id="version-label">Choose Version</InputLabel>
                                <Select
                                    labelId="version-label"
                                    value={country}
                                    label="Choose Version"
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setCountry(val);
                                        if (logoApplied)
                                            setHelpSnack({
                                                open: true,
                                                ok: true,
                                                msg: "Template changed — review logo position.",
                                            });
                                    }}
                                >
                                    <MenuItem value="uk">UK</MenuItem>
                                    <MenuItem value="us">US</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                label="Company Name"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                placeholder="Enter company name"
                                fullWidth
                            />

                            {/* Employer Code + copy */}
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    label="Employer Code"
                                    value={generatePromoCode(companyName)}
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                        sx: { color: "primary.main", fontWeight: 700 },
                                    }}
                                    helperText="Auto-generated from company name"
                                />
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => navigator.clipboard.writeText(generatePromoCode(companyName))}
                                >
                                    Copy
                                </Button>
                            </Stack>

                            {/* Exclusive offer toggle & single text box */}
                            <Divider sx={{ my: 1.5 }} />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={customizeOffer}
                                        onChange={(e) => setCustomizeOffer(e.target.checked)}
                                    />
                                }
                                label="Add an exclusive offer"
                            />

                            {customizeOffer && (
                                <Stack spacing={1.5}>
                                    <TextField
                                        label="Exclusive offer text"
                                        value={exclusiveOfferText}
                                        onChange={(e) => setExclusiveOfferText(e.target.value)}
                                        fullWidth
                                        helperText='Shown on the artwork (white, centered, wraps; 0.75 inch side margins).'
                                    />
                                </Stack>
                            )}
                            <Divider sx={{ my: 1.5 }} />

                            {/* Upload + Skip (info icon INSIDE upload button) */}
                            <Stack direction="row" spacing={1} alignItems="center">
                                {/* Upload button wrapper becomes the positioning context */}
                                <Box sx={{ position: "relative", flex: 1 }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        fullWidth
                                        disabled={skipLogo}
                                        sx={{ pr: 5 }} // padding so label doesn't overlap icon
                                    >
                                        {logoFile ? "Change Logo" : "Upload Logo"}
                                        <input
                                            type="file"
                                            accept="image/png, image/jpeg"
                                            hidden
                                            onChange={fileInputOnChange}
                                        />
                                    </Button>

                                    <Tooltip
                                        placement="left"
                                        title={
                                            <div>
                                                <div>
                                                    <strong>Logo tips</strong>
                                                </div>
                                                <ul style={{ paddingLeft: 16, margin: "6px 0" }}>
                                                    <li>
                                                        Use a <em>PNG</em> with transparent background.
                                                    </li>
                                                    <li>Avoid pure black logos for better contrast.</li>
                                                    <li>
                                                        Place within the <strong>top 25%</strong> dashed area.
                                                    </li>
                                                    <li>Suggested width ~ 800–1200px.</li>
                                                </ul>
                                            </div>
                                        }
                                    >
                                        {/* wrapper stops the label click opening file picker */}
                                        <Box
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                position: "absolute",
                                                right: 8,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                zIndex: 2,
                                            }}
                                        >
                                            <IconButton size="small" aria-label="Logo tips" disabled={skipLogo}>
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                </Box>

                                <FormControlLabel
                                    sx={{ ml: 0.5, whiteSpace: "nowrap" }}
                                    control={
                                        <Switch
                                            checked={skipLogo}
                                            onChange={(e) => {
                                                const on = e.target.checked;
                                                setSkipLogo(on);

                                                // If skipping, clear any existing logo state and exit edit mode
                                                if (on) {
                                                    setLogoFile(null);
                                                    setLogoURL(null);
                                                    setLogoState(DEFAULT_LOGO_STATE);
                                                    setAppliedLogoState(DEFAULT_LOGO_STATE);
                                                    setEditMode(false);
                                                    setLogoApplied(false);
                                                    setLockAspect(true);
                                                }
                                            }}
                                            color="primary"
                                        />
                                    }
                                    label="Skip"
                                />
                            </Stack>

                            {/* Logo controls (hidden when skipping) */}
                            {!skipLogo && (
                                <>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={lockAspect}
                                                onChange={(e) => setLockAspect(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Lock image aspect ratio when resizing"
                                    />

                                    {logoFile && editMode && (
                                        <LoadingButton loading={false} variant="contained" onClick={handleApplyLogo}>
                                            Apply Logo
                                        </LoadingButton>
                                    )}
                                </>
                            )}

                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setLogoState(DEFAULT_LOGO_STATE);
                                        setAppliedLogoState(DEFAULT_LOGO_STATE);
                                        setEditMode(false);
                                        setLogoApplied(false);
                                    }}
                                    disabled={!logoFile || skipLogo}
                                >
                                    Reset Image
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => {
                                        setCountry(DEFAULT_COUNTRY);
                                        setCompanyName("");
                                        setSkipLogo(false);
                                        setLogoFile(null);
                                        setLogoURL(null);
                                        setLogoState(DEFAULT_LOGO_STATE);
                                        setAppliedLogoState(DEFAULT_LOGO_STATE);
                                        setEditMode(false);
                                        setLogoApplied(false);
                                        setLockAspect(true);
                                    }}
                                >
                                    Clear Form
                                </Button>

                                {canDownload && (
                                    <LoadingButton
                                        loading={false}
                                        variant="contained"
                                        onClick={downloadPDF}
                                        startIcon={<DownloadIcon />}
                                        disabled={!hasCompany}
                                    >
                                        Download PDF
                                    </LoadingButton>
                                )}
                            </Stack>
                        </Stack>
                    </Paper>

                    {/* Preview panel with Drag & Drop (no click) */}
                    <Paper sx={{ p: 2, flex: 1.2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Preview
                        </Typography>

                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Box
                                {...getRootProps()}
                                ref={pageRef}
                                sx={{
                                    width: previewBoxDims.w,
                                    height: previewBoxDims.h,
                                    position: "relative",
                                    background: theme.palette.background.paper,
                                    borderRadius: 2,
                                    boxShadow:
                                        mode === "dark"
                                            ? "0 10px 30px rgba(0,0,0,0.35)"
                                            : "0 10px 30px rgba(0,0,0,0.08)",
                                    overflow: "hidden",
                                    outline: "2px dashed transparent",
                                    "&:hover": { outlineColor: "transparent" },
                                    ...(isDragActive && {
                                        outlineColor: theme.palette.primary.main,
                                        outlineOffset: "4px",
                                        outlineWidth: "3px",
                                    }),
                                }}
                                title="Drag a logo image into this area (or use the Upload button)"
                            >
                                <input {...getInputProps()} />

                                {/* Top-25% guide (dashed) */}
                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        width: "100%",
                                        height: allowedLogoBottom,
                                        borderBottom: "2px dashed rgba(226,35,26,0.5)",
                                        pointerEvents: "none",
                                        zIndex: 2,
                                    }}
                                />

                                {/* Use template picker (respects exclusive offer toggle) */}
                                <Document file={getTemplatePath(country, exclusiveOn)}>
                                    <Page pageNumber={1} width={previewBoxDims.w} onRenderSuccess={onPageRenderSuccess} />
                                </Document>

                                {!skipLogo && !logoFile && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            left: 12,
                                            bottom: 12,
                                            right: 12,
                                            color: "text.secondary",
                                            fontSize: 13,
                                            px: 1,
                                            py: 1,
                                            textAlign: "center",
                                            background:
                                                mode === "dark" ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.65)",
                                            borderRadius: 1,
                                        }}
                                    >
                                        Drag a company logo (PNG/JPG) into this area or use “Upload Logo”
                                    </Box>
                                )}

                                {/* Editable Logo — hard stop at 25% on drag/resize */}
                                {!skipLogo && logoFile && editMode && (
                                    <Rnd
                                        bounds="parent"
                                        size={{ width: logoState.width, height: logoState.height }}
                                        position={{ x: logoState.x, y: logoState.y }}
                                        onDrag={(e, d) => {
                                            setLogoState((s) => {
                                                const p = clampDrag(d.x, d.y, s.width, s.height);
                                                return { ...s, ...p };
                                            });
                                        }}
                                        onDragStop={(e, d) => {
                                            setLogoState((s) => {
                                                const p = clampDrag(d.x, d.y, s.width, s.height);
                                                return { ...s, ...p };
                                            });
                                        }}
                                        onResize={(e, dir, ref, delta, pos) => {
                                            const next = clampResize(
                                                parseInt(ref.style.width, 10),
                                                parseInt(ref.style.height, 10),
                                                pos.x,
                                                pos.y
                                            );
                                            setLogoState(next);
                                        }}
                                        onResizeStop={(e, dir, ref, delta, pos) => {
                                            const next = clampResize(
                                                parseInt(ref.style.width, 10),
                                                parseInt(ref.style.height, 10),
                                                pos.x,
                                                pos.y
                                            );
                                            setLogoState(next);
                                        }}
                                        lockAspectRatio={lockAspect}
                                        style={{
                                            position: "absolute",
                                            zIndex: 10,
                                            border: "2px dashed #e2231a",
                                            background: isPngLogo ? "transparent" : "rgba(255,255,255,0.7)",
                                            cursor: "move",
                                        }}
                                    >
                                        <img
                                            src={logoURL}
                                            alt="logo"
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                                pointerEvents: "none",
                                            }}
                                        />
                                    </Rnd>
                                )}

                                {/* Static Logo */}
                                {!skipLogo && logoFile && logoApplied && !editMode && (
                                    <img
                                        src={logoURL}
                                        alt="logo"
                                        style={{
                                            position: "absolute",
                                            left: Math.max(0, Math.min(appliedLogoState.x, previewBoxDims.w - appliedLogoState.width)),
                                            top: Math.max(0, Math.min(appliedLogoState.y, allowedLogoBottom - appliedLogoState.height)),
                                            width: appliedLogoState.width,
                                            height: Math.min(appliedLogoState.height, allowedLogoBottom - appliedLogoState.y),
                                            objectFit: "contain",
                                            background: isPngLogo ? "transparent" : "rgba(255,255,255,0.7)",
                                            cursor: "pointer",
                                            zIndex: 10,
                                        }}
                                        onClick={handleLogoClick}
                                        title="Click to move/resize logo"
                                    />
                                )}

                                {/* Exclusive offer overlay (single box) */}
                                {customizeOffer && (
                                    <>
                                        {(() => {
                                            const W = previewBoxDims.w;
                                            const H = previewBoxDims.h;

                                            // Convert inches -> preview px (based on actual PDF size)
                                            const PAGE_W_IN = pageWidth / 72;
                                            const PAGE_H_IN = pageHeight / 72;
                                            const toPXw = (inch) => (inch / PAGE_W_IN) * W;
                                            const toPXh = (inch) => (inch / PAGE_H_IN) * H;

                                            // Side margins: 0.75"
                                            const SIDE_MARGIN_IN = 0.75;
                                            const sideMarginPx = toPXw(SIDE_MARGIN_IN);

                                            // Height budget for exclusive text block (~1.1")
                                            const BLOCK_H_IN = 1.1;
                                            const blockHeightPx = toPXh(BLOCK_H_IN);

                                            // Base Y (inches from top). Adjust with exclusiveYOffset (px).
                                            const BASE_TOP_IN = 10.0;
                                            const topPx = toPXh(BASE_TOP_IN) + exclusiveYOffset;

                                            const leftPx = sideMarginPx;
                                            const widthPx = W - sideMarginPx * 2;

                                            const fit = fitMultilineFont(exclusiveOfferText, 12, widthPx, blockHeightPx, 4);

                                            const blockTop = Math.max(0, Math.min(topPx, H - blockHeightPx));
                                            const textTop =
                                                blockTop + (blockHeightPx - fit.lineHeight * (fit.lines.length || 1)) / 2;

                                            const textCommon = {
                                                position: "absolute",
                                                left: leftPx,
                                                top: textTop,
                                                width: widthPx,
                                                color: "#fff",
                                                textAlign: "center",
                                                textShadow: "0 1px 2px rgba(0,0,0,0.65), 0 0 1px rgba(0,0,0,0.45)",
                                                pointerEvents: "none",
                                                fontWeight: 700,
                                                letterSpacing: 0.2,
                                                lineHeight: `${fit.lineHeight}px`,
                                                fontSize: fit.size,
                                                zIndex: 5,
                                                whiteSpace: "pre-wrap",
                                                overflow: "hidden",
                                            };

                                            return (
                                                <Typography sx={textCommon}>
                                                    {fit.lines.length ? fit.lines.join("\n") : exclusiveOfferText}
                                                </Typography>
                                            );
                                        })()}
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Stack>
            </Container>

            {/* Floating Help button */}
            <Tooltip title="Get help from IT Support">
                <Fab
                    color="default"
                    onClick={handleOpenHelp}
                    sx={{
                        position: "fixed",
                        right: 24,
                        bottom: 24,
                        background: theme.palette.background.paper,
                        border: "1px solid rgba(0,0,0,0.1)",
                    }}
                    aria-label="help"
                >
                    <HelpOutlineIcon />
                </Fab>
            </Tooltip>

            {/* Help Drawer */}
            <Drawer
                anchor="right"
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                PaperProps={{ sx: { width: 440 } }}
            >
                <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", height: "100%" }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        Contact IT Support
                    </Typography>

                    <Paper variant="outlined" sx={{ p: 1.25, mb: 2 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <Chip size="small" label={`User: ${account?.name || "User"}`} />
                            <Chip size="small" label={`Job: ${jobTitle || "-"}`} />
                            <Chip size="small" label={`UPN: ${account?.username || "-"}`} />
                            <Chip size="small" label={`Region: ${country?.toUpperCase()}`} />
                            <Chip size="small" label={`Company: ${companyName || "-"}`} />
                            <Chip size="small" label={`Promo: ${generatePromoCode(companyName) || "-"}`} />
                            <Chip size="small" label={`Skip Logo: ${skipLogo ? "Yes" : "No"}`} />
                            <Chip size="small" label="Subject: Support Request – PDF Generator" />
                        </Box>
                    </Paper>

                    <Divider sx={{ mb: 2 }} />

                    <TextField
                        label="Describe the issue"
                        value={helpBody}
                        onChange={(e) => setHelpBody(e.target.value)}
                        fullWidth
                        multiline
                        minRows={8}
                        placeholder="What happened? Any error text, steps to reproduce, time of issue…"
                        helperText={`${helpBody.length}/2000`}
                        inputProps={{ maxLength: 2000 }}
                    />

                    <Box sx={{ mt: "auto", display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Button variant="text" onClick={() => setHelpOpen(false)} disabled={helpSending}>
                            Cancel
                        </Button>
                        <LoadingButton loading={helpSending} variant="contained" onClick={handleSendHelp}>
                            {helpSending ? "Sending…" : "Send to IT Support"}
                        </LoadingButton>
                    </Box>
                </Box>
            </Drawer>

            {/* SharePoint Upload Popout */}
            <Dialog
                open={uploadDialogOpen}
                onClose={() => !spUploading && setUploadDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Upload to SharePoint?</DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ mb: 1.5 }}>
                        Your PDF <strong>{uploadFilename}</strong> has been downloaded.
                    </Typography>
                    <Typography sx={{ mb: 2 }}>Would you like to upload it to the Marketing library under:</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                        <Typography sx={{ fontSize: 14 }}>
                            <code>
                                /Assets/Regional Marketing Assets/B2B/User Generated Corporate PDF&apos;s/
                                {companyName || "Company"}
                            </code>
                        </Typography>
                    </Paper>
                    <Typography variant="body2" color="text.secondary">
                        The app will create the company folder (if needed) and overwrite any existing file with the same name.
                    </Typography>
                    {spUploading && <LinearProgress sx={{ mt: 2 }} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadDialogOpen(false)} disabled={spUploading}>
                        Not now
                    </Button>
                    <LoadingButton
                        loading={spUploading}
                        variant="contained"
                        onClick={uploadToSharePoint}
                        disabled={!companyName.trim()}
                    >
                        Upload to SharePoint
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Snackbars */}
            <Snackbar
                open={helpSnack.open}
                autoHideDuration={4000}
                onClose={() => setHelpSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setHelpSnack((s) => ({ ...s, open: false }))}
                    severity={helpSnack.ok ? "success" : "error"}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {helpSnack.msg}
                </Alert>
            </Snackbar>
        </ThemeProvider>
    );

    // ===== PDF generation (moved to bottom so it can close over latest state) =====
    async function downloadPDF() {
        if (!canDownload || !companyName.trim()) return;

        const templateUrl = getTemplatePath(country, exclusiveOn);

        const pdfBytes = await (await fetch(templateUrl)).arrayBuffer();
        const pdfDoc = await PDFDocument.load(pdfBytes);
        pdfDoc.registerFontkit(fontkit);

        const fontBytes = await fetch(`${PUBLIC}/Montserrat-light.ttf`).then((res) => res.arrayBuffer());
        const customFont = await pdfDoc.embedFont(fontBytes);

        const page = pdfDoc.getPage(0);
        const [pdfW, pdfH] = [page.getWidth(), page.getHeight()];

        // scale preview -> PDF
        const { offsetWidth: rw, offsetHeight: rh } = pageRef.current;
        const scaleX = pdfW / rw;
        const scaleY = pdfH / rh;

        // Only embed/draw logo if not skipping and a logo exists & has been applied
        if (!skipLogo && logoFile && logoApplied) {
            const logoBytes = await logoFile.arrayBuffer();
            const embeddedLogo = logoFile.type.includes("png")
                ? await pdfDoc.embedPng(logoBytes)
                : await pdfDoc.embedJpg(logoBytes);

            const x = appliedLogoState.x * scaleX;
            const y = pdfH - (appliedLogoState.y + appliedLogoState.height) * scaleY;
            const w = appliedLogoState.width * scaleX;
            const h = appliedLogoState.height * scaleY;

            page.drawImage(embeddedLogo, { x, y, width: w, height: h });
        }

        // Draw exclusive offer text (if enabled)
        if (exclusiveOn && exclusiveOfferText.trim()) {
            // Side margins: 0.75"
            const SIDE_MARGIN_IN = 0.75;
            const sideMarginPx = SIDE_MARGIN_IN * 72;
            // Block height ~1.1"
            const BLOCK_H_IN = 1.1;
            const blockHeightPx = BLOCK_H_IN * 72;

            // Base top in inches + the same pixel offset scaled to PDF space
            const BASE_TOP_IN = 10.0;
            const baseTopPx = BASE_TOP_IN * 72;
            const previewH = rh;
            const pdfYOffset = exclusiveYOffset * (pdfH / previewH);

            const leftPx = sideMarginPx;
            const widthPx = pdfW - sideMarginPx * 2;
            const topPx = Math.max(0, Math.min(baseTopPx + pdfYOffset, pdfH - blockHeightPx));

            // Fit text (approximate)
            const baseSize = 18;
            const lineHeight = baseSize * 1.16;
            let size = baseSize;

            const measure = (s, t) => customFont.widthOfTextAtSize(t, s);
            const maxHeight = blockHeightPx;
            const words = exclusiveOfferText.trim().split(/\s+/);

            const buildLines = (s) => {
                const lines = [];
                let line = "";
                for (const w2 of words) {
                    const cand = line ? `${line} ${w2}` : w2;
                    if (measure(s, cand) <= widthPx) line = cand;
                    else {
                        if (line) lines.push(line);
                        line = w2;
                    }
                }
                if (line) lines.push(line);
                return lines;
            };

            let lines = buildLines(size);
            while (lines.length * lineHeight > maxHeight && size > 10) {
                size -= 1;
                lines = buildLines(size);
            }

            const totalTextHeight = lines.length * lineHeight;
            let yStart = pdfH - topPx - (blockHeightPx - totalTextHeight) / 2 - size; // baseline
            const white = rgb(1, 1, 1);

            for (const line of lines) {
                const textWidth = customFont.widthOfTextAtSize(line, size);
                const xCentered = leftPx + (widthPx - textWidth) / 2;
                page.drawText(line, {
                    x: xCentered,
                    y: yStart,
                    size,
                    font: customFont,
                    color: white,
                });
                yStart -= lineHeight;
            }
        }

        // Existing promo placements (unchanged)
        const promoText = generatePromoCode(companyName);
        if (promoText) {
            const black = rgb(0, 0, 0);

            if (pdfDoc.getPageCount() >= 4) {
                const page4 = pdfDoc.getPage(3);
                const pdfH4 = page4.getHeight();
                const yBottomUp4 = pdfH4 - 787;
                page4.drawText(promoText, {
                    x: 810,
                    y: yBottomUp4,
                    size: 20,
                    font: customFont,
                    color: black,
                });
            }

            if (pdfDoc.getPageCount() >= 5) {
                const page5 = pdfDoc.getPage(4);
                const pdfH5 = page5.getHeight();
                const yBottomUp5 = pdfH5 - 437;
                page5.drawText(promoText, {
                    x: 303,
                    y: yBottomUp5,
                    size: 20,
                    font: customFont,
                    color: black,
                });
            }
        }

        const month = new Date().toLocaleString("default", { month: "long" });
        const nameForFile = companyName.trim() ? companyName.trim() : "Client";
        const filename = `UP Fitness - ${nameForFile} - ${month}.pdf`;

        // Save PDF with compression enabled to reduce file size (target ~30MB max)
        const pdfUint8 = await pdfDoc.save({
            useObjectStreams: true,      // Enables better compression
            addDefaultPage: false,       // Don't add unnecessary pages
            objectsPerTick: 50,          // Process in batches for stability
        });
        const blob = new Blob([pdfUint8], { type: "application/pdf" });

        // Download locally
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        // Store for SharePoint upload popout
        setUploadBlob(blob);
        setUploadFilename(filename);
        setUploadDialogOpen(true);

        await logEvent({
            event: "pdf_generated",
            company: nameForFile,
            promo: generatePromoCode(companyName),
            filename,
            by: account?.username,
            skipLogo,
        });
    }
}
